import { Box, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  capitalizeFirstLetter,
  convertDateTimetoEpoch,
  debounce,
  findAddress,
  formatISTtoTime,
  useAbort
} from '../../../../../utils/commonFunctions';

import './UpdateScheduleTrip.scss';

import {
  getExcelAction,
  getStandardVehicles,
  PvtGrpUpdate
} from '../../../redux/reducer/autoPlannerSlices/autoGenerateSlice';
import {
  updateCenter,
  updateZoom
} from '../../../../../common/redux/reducer/commonSlices/mapSlice';
import GoogleMap from '../../../../../common/components/maps/googlemap/GoogleMap';
import GoogleTripDirections from '../../../../../common/components/maps/googledirection/GoogleTripDirections';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import CustomSelect from '../../../../../common/components/customized/customselect/CustomSelect';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import PhoneNoTextField from '../../../../../common/components/customized/customtextfield/PhoneNoTextField';
import CustomRadioButton from '../../../../../common/components/customized/customradiobutton/CustomRadioButton';
import GoogleSearchBox from '../../../../../common/components/maps/googlesearchbox/GoogleSearchBox';
import CustomTimePicker from '../../../../../common/components/customized/customtimepicker/CustomTimePicker';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import {
  autoPlannerAgentAction,
  clearContactError,
  contactNumberValidation
} from '../../../redux/reducer/autoPlannerSlices/autoplanner';

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  autoplannerID: any;
  setTripMode?: any;
  tripMode?: string;
  selectedRow: any;
  excelPayload?: any;
  selectedTripRow: any;
  pageDetail: { pageNo: number; pageSize: number };
  Agents: { id: string; label: string }[];
  filterPayload?: any;
}

interface Select {
  id: string;
  label: string;
}

interface UpdatePayload {
  agentName: string;
  guestName: string;
  adultCount: number;
  childCount: number;
  refNumber: string;
  vehicleNumber?: string;
  source: string | null;
  startLat: number | null;
  startLng: number | null;
  tourName: string | null;
  time: string | null;
  driverName: string;
  passengerCount: number;
  driverNumber: number;
  filterPayload?: any;
}

const UpdatePVTGRP = ({
  isOpen,
  setIsOpen,
  selectedRow,
  selectedTripRow,
  pageDetail,
  autoplannerID,
  tripMode,
  setTripMode,
  excelPayload,
  filterPayload,
  Agents
}: Props) => {
  const [sourceAddress, setSourceAddress] = useState<any>(null);
  const [destinationAddress, setDestinationAddress] = useState<any>(null);

  const [tripMarker, setTripMarker] = useState<any>([]);
  const [external, setExternal] = useState<boolean>(true);
  const [isDrag, setIsDrag] = useState<boolean>(false);
  const [seatCapacity, setSeatCapacity] = useState<any>(0);
  const [validationErrors, setValidationErrorsMake] = useState<any>(null);
  const contactError = useAppSelector(state => state.ContactNumberValidation);

  const dispatch = useAppDispatch();

  const addData = useAppSelector(state => state.AutoPlannerAddTrip);
  const updateData = useAppSelector(state => state.AutoPlannerUpdateTrip);
  const { data: dropDownData, isLoading: dropdownLoading } = useAppSelector(
    state => state.getStandardVehicles
  );
  const AgentsDropdown = useAppSelector(state => state.autoPlannerAgent.data || []);
  const { isLoading } = useAppSelector(state => state.updatePvtGrpScheduled);

  const { data } = useAppSelector(state => state.auth);
  let role = data?.role;
  const createAbort = useAbort();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const schema = () =>
    yup.object().shape({
      agentName: yup
        .string()
        .required('Select Agent Name')
        .matches(/^[a-zA-Z0-9\s.-]*$/, 'Agent Name Can Only Contain Letters and Numbers'),
      guestName: yup.string().required('Enter Guest Name'),
      adultCount: yup
        .number()
        // .matches(/^\d+$/, 'Enter Valid Count')
        .required('Enter Adult Count')
        .min(1, 'Adult Count Must be Atleast 1')
        .typeError('Enter Adult Count'),
      phone: yup
        .string()
        .optional()
        .test(
          'phone-error',
          'Enter valid contact number',
          function (value: any, field: any) {
            if (
              validationErrors?.phone &&
              value &&
              validationErrors?.phone !== 'No Error'
            ) {
              return this.createError({ message: validationErrors?.phone });
            } else {
              return true;
            }
          }
        ),
      childCount: yup
        .number()
        .notRequired()
        .min(0, 'Child Count Cannot be Below 0')
        .max(50, 'Child Count Must Not Exceed 50')
        .typeError('Child Count Cannot be Below 0'),
      refNumber: yup.string().notRequired(),
      vehicle: yup.string().oneOf(['internal', 'external']).notRequired(),
      vehicleNumber: yup.string().when('vehicle', {
        is: 'internal',
        then: () => yup.string().required('Select vehicle number'),
        otherwise: () =>
          yup
            .string()
            .required('Enter vehicle number')
            .matches(/^[A-Za-z0-9]*$/, 'Only letters and numbers allowed without spaces')
            .test(
              'valid-length',
              'Enter a valid vehicle number',
              value => (value?.length || 0) >= 4
            )
            .max(20, 'Maximum 20 characters')
            .test('has-letter', 'Must include at least one letter', value =>
              /[A-Za-z]/.test(value || '')
            )
      }),
      driverName: yup.string().when('vehicle', {
        is: 'external',
        then: () => yup.string().required('Enter Driver Name'),
        otherwise: () => yup.string().notRequired()
      }),
      passengerCount: yup.string().when('vehicle', {
        is: 'external',
        then: () => yup.string().required('Enter Seat Capacity'),
        otherwise: () => yup.string().notRequired()
      }),
      driverNumber: yup.string().when('vehicle', {
        is: 'external',
        then: () =>
          yup
            .string()
            .required('Enter Contact Number')
            .test('phone-error', 'Enter valid contact number', function (value: any) {
              if (
                validationErrors?.driverNumber &&
                value &&
                validationErrors?.driverNumber !== 'No Error'
              ) {
                return this.createError({ message: validationErrors?.driverNumber });
              } else {
                return true;
              }
            }),
        otherwise: () => yup.string().notRequired()
      }),
      sourceAddress: yup
        .object()
        .shape({
          source: yup.string().required('Enter Pickup Location'),
          startLat: yup.number().required('Enter Latitude for Pickup Location'),
          startLng: yup.number().required('Enter Longitude for Pickup Location')
        })
        .test(
          'different-source-destination',
          'Pickup and drop should not be the same',
          function (value) {
            const tour = this.parent?.tourORdestination || '';
            if (tour && value?.source === tour) {
              return this.createError({
                path: `${this.path}.source`,
                message: 'Pickup and drop should not be the same'
              });
            }
            return true;
          }
        ),
      tourORdestination: yup.string().required('Choose Return Time'),
      time: yup.object().required('Choose Return Time')
    });

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    trigger,
    getValues,
    formState: { errors }
  } = useForm<any>({
    resolver: yupResolver(schema())
  });

  const agentNames =
    AgentsDropdown?.map((value: string) => ({
      id: value,
      label: capitalizeFirstLetter(value)
    })) || [];

  const standardVehicles =
    dropDownData?.length > 0
      ? dropDownData?.map((value: string, index: number) => ({
          id: value,
          label: value.toUpperCase()
        }))
      : [];
  if (selectedTripRow?.vehicleNumber !== 'Unassigned') {
    const vehicleNumber = selectedTripRow?.vehicleNumber;
    if (!standardVehicles?.some((vehicle: any) => vehicle?.id === vehicleNumber)) {
      standardVehicles.push({
        id: vehicleNumber,
        label: vehicleNumber?.toUpperCase()
      });
    }
  }

  const address = async (lat: any, lng: any) => {
    let fAdd = findAddress(lat, lng);
    return fAdd;
  };

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const onSubmit = async (params: any) => {
    let payload: any = {
      agentName: params.agentName,
      guestName: params.guestName,
      source: params.sourceAddress.source,
      startLat: params.sourceAddress.startLat,
      startLng: params.sourceAddress.startLng,
      destination: params?.destinationAddress?.destination,
      endLat: params.destinationAddress.endLat,
      endLng: params.destinationAddress.endLng,
      time: formatISTtoTime(params.time),
      childCount: params.childCount,
      ...(params.phone &&
        params.phone.includes('-') &&
        params.phone.split('-')[1] && { guestContactNumber: params.phone }),
      isCustomRoute: selectedTripRow?.isCustomRoute,
      seatingCapacity: params.passengerCount
        ? params.passengerCount
        : selectedTripRow.seatingCapacity,
      adultCount: params.adultCount,
      refNumber: params.refNumber,
      tourName: selectedTripRow.destination,
      vehicleNumber: params.vehicleNumber === 'Unassigned' ? null : params.vehicleNumber,
      driverName: params.driverName === 'Unassigned' ? null : params.driverName,
      contactNumber: params.driverNumber === 'Unassigned' ? null : params.driverNumber,
      isExternalVehicle: params.vehicle === 'internal' ? 0 : 1,
      journeyId: selectedTripRow.journeyId,
      driverID:
        selectedTripRow.driverID === 'Unassigned' ? null : selectedTripRow.driverID,
      modifyVehicleDriver: selectedTripRow.modifyVehicleDriver
    };

    const pageDetails = {
      ...pageDetail,
      ...filterPayload,
      autoplannerID: autoplannerID,
      tripMode: tripMode,
      id: selectedRow?.id
    };
    const data = { payload, pageDetails };
    const allPayload = { ...filterPayload, ...excelPayload };
    const action = await dispatch(PvtGrpUpdate(data));
    if (action.type === PvtGrpUpdate.fulfilled.type) {
      handleClose();
      if (excelPayload) dispatch(getExcelAction(allPayload));
    }
  };

  const handleMarkerDrag = async (e: any, marker: any) => {
    if (marker === 'source') {
      const newAddress = await address(e?.latLng?.lat(), e?.latLng?.lng());
      setSourceAddress({
        geometry: {
          location: {
            lat: () => {
              return e?.latLng?.lat();
            },
            lng: () => {
              return e?.latLng?.lng();
            }
          }
        },
        formatted_address: newAddress
      });
    } else if (marker === 'destination') {
      const newAddress = await address(e?.latLng?.lat(), e?.latLng?.lng());
      setDestinationAddress({
        geometry: {
          location: {
            lat: () => {
              return e?.latLng?.lat();
            },
            lng: () => {
              return e?.latLng?.lng();
            }
          }
        },
        formatted_address: newAddress
      });
    }
  };

  useEffect(() => {
    if (selectedTripRow) {
      if (selectedTripRow?.time) {
        const time = selectedTripRow?.time !== null ? selectedTripRow?.time : '00:00';
        setValue(
          'time',
          dayjs()
            .set('hour', time?.split(':')[0].trim())
            .set('minute', time?.split(':')[1].trim())
        );
      } else if (selectedTripRow?.returnTime) {
        const time = selectedTripRow?.time !== null ? selectedTripRow?.time : '00:00';
        setValue(
          'time',
          dayjs()
            .set('hour', time?.split(':')[0].trim())
            .set('minute', time?.split(':')[1].trim())
        );
      }
      setValue('agentName', selectedTripRow?.agentName);
      setValue(
        'vehicle',
        selectedTripRow?.isExternalVehicle === 1 ? 'external' : 'internal'
      );
      setExternal(selectedTripRow?.isExternalVehicle === 1 ? false : true);
      setValue('driverNumber', selectedTripRow?.driverContactNumber);
      setValue('passengerCount', selectedTripRow?.seatingCapacity);
      setValue('driverName', selectedTripRow?.driverName);
      setValue('guestName', selectedTripRow?.guestName);
      setValue('adultCount', selectedTripRow?.adultCount);
      setValue('childCount', selectedTripRow?.childCount);
      setValue('refNumber', selectedTripRow?.refNumber);
      setValue('vehicleNumber', selectedTripRow?.vehicleNumber);
      setValue('tourName', selectedTripRow?.tourName);
      setValue('tourORdestination', selectedTripRow.destination);
      setTripMarker([
        {
          id: 'source',
          lat: selectedTripRow?.startLat,
          lng: selectedTripRow?.startLng,
          name: 'trip',
          info: { Mylocation: 'source' }
        }
      ]);
      setSourceAddress({
        geometry: {
          location: {
            lat: () => {
              return selectedTripRow?.startLat;
            },
            lng: () => {
              return selectedTripRow?.startLng;
            }
          }
        },
        formatted_address: selectedTripRow?.source
      });
      setDestinationAddress({
        geometry: {
          location: {
            lat: () => {
              return selectedTripRow?.endLat;
            },
            lng: () => {
              return selectedTripRow?.endLng;
            }
          }
        },
        formatted_address: selectedTripRow?.destination
      });
    }
  }, [selectedTripRow]);

  useEffect(() => {
    setValue('sourceAddress', {
      source: sourceAddress?.formatted_address,
      startLat: sourceAddress?.geometry?.location?.lat(),
      startLng: sourceAddress?.geometry?.location?.lng()
    });
    clearErrors('sourceAddress');
    const prevMarkers = tripMarker;
    if (prevMarkers?.length <= 1 && sourceAddress) {
      setTripMarker([
        {
          id: 'source',
          lat: sourceAddress?.geometry?.location?.lat(),
          lng: sourceAddress?.geometry?.location?.lng(),
          name: 'trip',
          info: { Mylocation: 'source' }
        }
      ]);
    } else if (sourceAddress) {
      setTripMarker((prev: any) => {
        let updatedMarkers = prev?.map((marker: any) =>
          marker?.id === 'source'
            ? {
                ...marker,
                lat: sourceAddress?.geometry?.location?.lat(),
                lng: sourceAddress?.geometry?.location?.lng()
              }
            : marker
        );
        return updatedMarkers;
      });
    }
  }, [sourceAddress]);

  useEffect(() => {
    setValue('destinationAddress', {
      destination: destinationAddress?.formatted_address,
      endLat: destinationAddress?.geometry?.location?.lat(),
      endLng: destinationAddress?.geometry?.location?.lng()
    });
    clearErrors('destinationAddress');
    const prevMarkers = tripMarker;
    if (prevMarkers?.length <= 1 && destinationAddress) {
      setTripMarker([
        {
          id: 'destination',
          lat: destinationAddress?.geometry?.location?.lat(),
          lng: destinationAddress?.geometry?.location?.lng(),
          name: 'trip',
          info: { Mylocation: 'destination' }
        }
      ]);
    } else if (destinationAddress) {
      setTripMarker((prev: any) => {
        let updatedMarkers = prev?.map((marker: any) =>
          marker?.id === 'destination'
            ? {
                ...marker,
                lat: destinationAddress?.geometry?.location?.lat(),
                lng: destinationAddress?.geometry?.location?.lng()
              }
            : marker
        );
        return updatedMarkers;
      });
    }
  }, [destinationAddress]);

  useEffect(() => {
    if (destinationAddress && sourceAddress) {
      setTripMarker([
        {
          id: 'source',
          lat: sourceAddress?.geometry?.location?.lat(),
          lng: sourceAddress?.geometry?.location?.lng(),
          name: 'trip',
          info: { Mylocation: 'source' }
        },
        {
          id: 'destination',
          lat: destinationAddress?.geometry?.location?.lat(),
          lng: destinationAddress?.geometry?.location?.lng(),
          name: 'trip',
          info: { Mylocation: 'destination' }
        }
      ]);
    }
  }, [sourceAddress, destinationAddress]);

  useEffect(() => {
    if (tripMarker[0]?.lat && tripMarker[0]?.lng) {
      dispatch(updateCenter(tripMarker));
      dispatch(updateZoom(tripMarker));
    }
  }, [tripMarker]);

  useEffect(() => {
    if (data?.userId && data?.role === 'ROLE_AGENT') setValue('agentName', data?.userId);
  }, [data]);

  useEffect(() => {
    const signal = createAbort().abortCall.signal;
    dispatch(autoPlannerAgentAction(signal));
  }, []);

  const handleValidate = useCallback(
    debounce(async (event: any, field: any) => {
      let action1 = await dispatch(contactNumberValidation(event));
      setValidationErrorsMake((prev: any) => ({
        ...prev,
        [field]: action1?.payload?.status === 200 ? 'No Error' : action1?.payload
      }));
    }),
    []
  );

  useEffect(() => {
    return () => {
      dispatch(clearContactError());
    };
  }, []);

  useEffect(() => {
    if (validationErrors?.phone) {
      trigger('phone');
    }
    if (validationErrors?.driverNumber) {
      trigger('driverNumber');
    }
  }, [validationErrors]);

  useEffect(() => {
    const payload = {
      autoplannerID: autoplannerID,
      tripMode: tripMode,
      tourName: selectedTripRow?.journeyId,
      totalPassengers: selectedTripRow?.adultCount + selectedTripRow?.childCount,
      vehicleNumber:
        selectedTripRow?.vehicleNumber === 'Unassigned'
          ? ''
          : selectedTripRow?.vehicleNumber,
      journeyId: selectedTripRow?.journeyId
    };
    dispatch(getStandardVehicles(payload));
  }, []);

  return (
    <Box className='add-trip-component'>
      <Dialog
        open={isOpen}
        fullScreen
        sx={{ padding: 3, zIndex: 1, position: 'absolute' }}
        className='add-trip-dialog animate__animated animate__zoomIn'
        BackdropProps={{
          invisible: true
        }}
      >
        <DialogContent
          sx={{ overflow: 'hidden', padding: 0 }}
          className='add-trip-dialog-content'
        >
          <Grid
            container
            sx={{
              flexDirection: { xs: 'column-reverse', md: 'row', sm: 'row' }
            }}
            className='add-trip-grid-container'
          >
            <Grid item xs={12} md={8} sm={8} lg={9} sx={{ overflow: 'hidden' }} p={2}>
              <Box
                sx={{
                  width: '100%',
                  height: '87vh',
                  overflow: { xs: 'scroll', md: 'hidden' },
                  borderRadius: '10px'
                }}
              >
                <GoogleMap>
                  <GoogleTripDirections
                    locations={tripMarker}
                    isDraggable={false}
                    setTripMarker={setTripMarker}
                    handleMarker={handleMarkerDrag}
                    showPath={true}
                  />
                </GoogleMap>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sm={4} lg={3} className='add-trip-grid-item'>
              <Box className='add-trip-header'>
                <Typography className='add-booking-title'>
                  {'Update Scheduled Booking'}
                </Typography>
              </Box>
              <Box
                className='closeIcon icon-position d-flex close-icon-index'
                onClick={() => handleClose()}
              >
                <CustomIconButton category='CloseValue' />
              </Box>
              <Box
                className='add-trip-form animate__animated animate__zoomIn animate__slow'
                component='form'
                sx={{
                  height: 'fit-content',
                  maxHeight: '82vh',
                  overflowY: 'auto',
                  paddingRight: '15px'
                }}
                onSubmit={handleSubmit(onSubmit)}
              >
                <CustomSelect
                  id='agent-name'
                  control={control}
                  name='agentName'
                  label='Agent Name'
                  placeholder='Select Agent Name'
                  options={agentNames}
                  isDisabled={
                    data?.role !== 'ROLE_OPERATOR' &&
                    data?.role !== 'ROLE_AUTOPLANNER_ADMIN'
                  }
                />
                <CustomTextField
                  id='guest-name'
                  control={control}
                  name='guestName'
                  label='Guest Name'
                  placeholder='Enter Guest Name'
                />
                <Controller
                  name='phone'
                  control={control}
                  defaultValue={selectedTripRow?.guestContactNumber || ''}
                  rules={{ required: true }}
                  render={({ field }: any) => (
                    <PhoneNoTextField
                      {...field}
                      setValue={setValue}
                      isOptional={true}
                      style='share'
                      label='Contact Number'
                      error={Boolean(errors?.phone?.message)}
                      helperText={errors.phone?.message}
                      onChange={(e: any) => {
                        if (e) {
                          const emptySpace = /^.+\s.+$/g;
                          if (e && !emptySpace.test(e)) {
                            handleValidate(e, field?.name);
                          }
                        } else {
                          setValue('phone', '');
                        }
                        trigger('phone');
                      }}
                    />
                  )}
                />
                <Grid container spacing={1} className='adult-count-field'>
                  <Grid item sm={6}>
                    <CustomTextField
                      id='adult-count'
                      control={control}
                      type='number'
                      name='adultCount'
                      label='Adult Count'
                      placeholder='Enter Count'
                    />
                  </Grid>
                  <Grid item sm={6} className='child-count-field'>
                    <CustomTextField
                      id='child-count'
                      control={control}
                      type='number'
                      name='childCount'
                      label='Child Count'
                      placeholder='Enter Count'
                    />
                  </Grid>
                </Grid>
                <CustomTextField
                  id='reference-no'
                  control={control}
                  name='refNumber'
                  label='Reference No.'
                  placeholder='Enter Reference No.'
                />
                <CustomRadioButton
                  name='vehicle'
                  control={control}
                  radiobutton1='Internal Vehicle'
                  radiobutton2='External Vehicle'
                  value1='internal'
                  value2='external'
                  value={getValues('vehicle')}
                  onChangeCallback={(e: any) => {
                    setValue('vehicle', e.target.value);
                    clearErrors('vehicleNumber');
                    if (e.target.value === 'internal') {
                      setExternal(true);
                      setIsDrag(false);
                      setValue('vehicleNumber', '');
                      setValue('driverName', '');
                      setValue('passengerCount', '');
                      setValue('driverNumber', '');
                    } else {
                      setValidationErrorsMake((prev: any) => ({
                        ...prev,
                        driverNumber: null
                      }));
                      clearErrors('driverNumber');
                      setExternal(false);
                      setIsDrag(true);
                      setValue('vehicleNumber', '');
                      setValue('driverName', '');
                      setValue('passengerCount', '');
                      setValue('driverNumber', '');
                    }
                  }}
                />
                {external ? (
                  <>
                    <CustomSelect
                      id='vehicleNumber'
                      control={control}
                      name='vehicleNumber'
                      label=' Vehicle Number'
                      placeholder='Select vehicle number'
                      options={standardVehicles}
                      loading={dropdownLoading}
                    />
                  </>
                ) : (
                  <>
                    <Box>
                      <Box sx={{ marginBottom: '10px' }}>
                        <CustomTextField
                          id='vehicleNumber'
                          control={control}
                          name='vehicleNumber'
                          label='Vehicle Number'
                          placeholder='Enter external vehicle number'
                          onChangeCallback={e => {
                            setValue('vehicleNumber', e);
                          }}
                        />
                      </Box>
                      <Box sx={{ marginBottom: '10px' }}>
                        <CustomTextField
                          id='driverName'
                          control={control}
                          name='driverName'
                          label='Driver Name'
                          placeholder='Enter driver name'
                          onChangeCallback={e => {
                            setValue('driverName', e);
                          }}
                        />
                      </Box>
                      <Box sx={{ marginBottom: '10px' }}>
                        <CustomTextField
                          id='count'
                          control={control}
                          type='number'
                          name='passengerCount'
                          disabled={selectedRow ? true : false}
                          label='Seat Capacity'
                          placeholder='Count'
                          onChangeCallback={e => {
                            setSeatCapacity(e);
                            setValue('passengerCount', e);
                            clearErrors('passengerCount');
                          }}
                        />
                      </Box>
                      <Box sx={{ marginBottom: '10px' }}>
                        <Controller
                          name='driverNumber'
                          control={control}
                          defaultValue={selectedTripRow?.driverContactNumber || '+65'}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <PhoneNoTextField
                              {...field}
                              setValue={setValue}
                              country={'sg'}
                              disableCountry={true}
                              style='share'
                              label='Contact Number'
                              error={errors?.driverNumber?.message ? true : false}
                              helperText={errors?.driverNumber?.message}
                              onChange={(e: any) => {
                                if (e) {
                                  const emptySpace = /^.+\s.+$/g;
                                  if (e && !emptySpace.test(e)) {
                                    handleValidate(e, field?.name);
                                  }
                                } else {
                                  setValue('driverNumber', '');
                                }
                                trigger('driverNumber');
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </>
                )}
                <GoogleSearchBox
                  id='search-source'
                  name='sourceAddress'
                  control={control}
                  label='Pickup Location'
                  placeholder='Search Location..'
                  value={sourceAddress?.formatted_address}
                  setAddress={setSourceAddress}
                  disabled={true}
                  country='sg'
                />
                <CustomTextField
                  id='search-destination'
                  control={control}
                  name='tourORdestination'
                  placeholder='Enter tour name'
                  disabled={true}
                  label={
                    selectedTripRow?.isCustomRoute === 0 ? 'Tour Name ' : 'Drop Location'
                  }
                />
                <CustomTimePicker
                  id='time'
                  name='time'
                  control={control}
                  isDisabled={true}
                  label='Time'
                  values={getValues('time')}
                />
                <Box mt={3} sx={{ textAlign: 'right' }}>
                  <CustomButton
                    category='Cancel'
                    className='cancel'
                    sx={{ marginRight: '10px' }}
                    onClick={() => handleClose()}
                  />
                  <CustomButton
                    category={'Save'}
                    loading={isLoading}
                    className='saveChanges'
                    type='submit'
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UpdatePVTGRP;
